import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Box,
  CircularProgress,
  Fab,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import LoadingBackdrop from '@/components/atoms/LoadingBackdrop';
import NetworkAlert from '@/components/atoms/NetworkAlerts';
import { useOpenNetworkAlert } from '@/components/hooks/useOpenNetworkAlert';
import Comment from '@/components/molecules/Comment';
import HintBalloon from '@/components/molecules/HintBalloon';
import SystemMessage from '@/components/molecules/SystemMessage';
import { MyMessagePosition } from '@/components/organisms/chat/ChatFooterMicDual';
import { ChatMessageItem } from '@/components/organisms/chat/ChatMessageCache';
import { ChatReaction } from '@/components/organisms/chat/ChatReactionList';
import { useProjectionMode } from '@/components/organisms/chat/useProjectionMode';
import {
  ChatMessageLanguage,
  ChatReactionType,
  ChatSessionUser,
  ChatUserRole,
} from '@/types/graphql';
import SystemCommand from '@/types/SystemCommand';
import { isChatEditor, isChatHost } from '@/utils/userRole';

const commentMenuHintEnabledAtom = atomWithStorage<boolean>(
  'COMMENT_MENU_HINT_ENABLED',
  true
);

const ChatBody = (props: {
  messages: [number, ChatMessageItem][];
  getIsContinuedMessage: (key: number, msg: ChatMessageItem) => boolean; // 前のメッセージが同一ユーザーかどうかを返してもらう
  userId: string;
  language: string;
  subUserLanguage?: string;
  reverseLanguage?: string;
  isOnReverseTranslation?: boolean;
  loading: boolean;

  canGuideControl?: boolean;
  dualLangTranslation?: boolean;
  myMessagePosition: MyMessagePosition;

  onClickInvite: () => void;
  onClickClearMessages: () => void;
  onClickLeaveSession: () => void;

  loadingPrevious: boolean;
  canLoadPrevious: boolean;
  loadPrevious: () => Promise<ChatMessageItem[]>;

  getUser: (id: string) => ChatSessionUser | undefined;
  onEditMessage: (msg: string, isSubUserSend: boolean) => void;
  onReplyMessage: (
    commentId: string,
    userId: string,
    message: string,
    isSubUserSend?: boolean,
    isMine?: boolean
  ) => void;
  onDeleteMessage: (commentId: string) => Promise<void>;
  onPlayMessage?: (
    text: string,
    language: string,
    messageId: string,
    voiceRate: number
  ) => void;
  onStopMessage: () => void;
  onReaction: (commentId: string, type: ChatReactionType) => Promise<void>;
  onOpenReaction: (reactions: ChatReaction[]) => void;
  onCheckFlag?: (commentId: string, check: boolean) => void;
  playingMessageId: string | undefined;
  replaceNgWord: (msg: string) => string;
  reactionIsOnlyQuestion: boolean;

  // バケツリレー何とかしたい
  chatRoomId: string;
  sessionEntryCode: string;
  userRole: ChatUserRole;
  isGuestInputRestricted: boolean;
  isHiddenInformation: boolean;
}) => {
  const { t } = useTranslation('chat');
  const [isAtBottom, setIsAtBottom] = useState(true);
  const isOpenedAlert = useOpenNetworkAlert();

  const chatbody = document.getElementById('chatbody');
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  const [hintAnchor, setHintAnchor] = useState<HTMLElement | null>(null);
  const [hintIsRight, setHintIsRight] = useState(false);
  const hintIndex = useRef(0);
  const [visibleRange, setVisibleRange] = useState({
    startIndex: 0,
    endIndex: 0,
  });

  const [commentMenuHintEnabled, setCommentMenuHintEnabled] = useAtom(
    commentMenuHintEnabledAtom
  );

  const { selectedProjectionLanguage, isProjectionMode } = useProjectionMode();

  const userRole = props.userRole;
  const getUser = props.getUser;
  const getUserName = useCallback(
    (id: string) => {
      const user = getUser(id);
      if (userRole === ChatUserRole.Guest) {
        if (
          user?.userRole === ChatUserRole.Guide ||
          user?.userRole === ChatUserRole.TenantCompanyAdmin
        ) {
          return user.userName || 'Unknown';
        }
        return undefined; //skip Guest
      }
      return user?.userName || 'Unknown';
    },
    [getUser, userRole]
  );

  // メッセージが更新された際、非表示のメッセージを除外したリストを生成する。
  // 本来リストは100万件あっても処理できるが、本処理をここに入れると1万件くらいが上限かも。
  // ちゃんと実装するには、ChatMessages 内部で専用のリストを持たせる必要がある。
  // また、同様の判定処理が SystemComponent 内にもあるので何とかしたい。
  const adjustedMessages = useMemo(() => {
    return props.messages.filter((x) => {
      const msg = x[1];
      const isSystem = msg.userId === '00000000-0000-0000-0000-000000000000';
      if (isSystem === false) return true;
      if (isProjectionMode) return false;

      const command = JSON.parse(msg.languages[0].message) as SystemCommand;
      const canGuideControl = isChatHost(props.userRole);
      if (command.commandType === 'clearChat' && canGuideControl) {
        return true;
      } else if (command.commandType === 'muteUserMic') {
        return true;
      } else if (command.commandType === 'joinUser') {
        return getUserName(command.userId) != null;
      } else if (command.commandType === 'leaveUser') {
        return getUserName(command.userId) != null;
      } else {
        return false;
      }
    });
  }, [props.messages, props.userRole, isProjectionMode, getUserName]);

  // スクロールが最上位にいて、過去のデータが読める場合は、読み込みを続ける
  const [isScrollTop, setIsScrollTop] = useState(false);
  const loadPrevious = props.loadPrevious;

  useEffect(() => {
    if (isScrollTop === false || props.canLoadPrevious !== true) return;
    let unmounted = false;
    const id = setInterval(async () => {
      // 1秒に5回呼ばれる
      if (unmounted) return; // 面倒なのでタイマー解除はアンマウント時の処理に任せる
      // isScrollTop && props.canLoadPrevious のはずなので何も考えずに呼び出し続ける
      // 排他処理などは呼び出し先の処理に全て委ねるので、awaitも不要
      loadPrevious();
    }, 200);
    return () => {
      unmounted = true;
      clearInterval(id);
    };
  }, [isScrollTop, props.canLoadPrevious, loadPrevious]);

  const goToBottom = useCallback(() => {
    virtuosoRef?.current?.scrollToIndex({
      align: 'end',
      behavior: 'smooth',
      index: 'LAST',
    });
  }, []);

  function getTransedDualMessage(msg: ChatMessageItem): string | undefined {
    const transLang = msg.isSubUserSend
      ? props.language
      : props.subUserLanguage;

    return msg.languages?.find((l) => l.language === transLang)?.message;
  }

  return (
    <>
      <Box position="relative">
        <Box position="absolute" width={1} height={1}>
          {props.canGuideControl && (
            <Stack
              direction={'row'}
              spacing={0.5}
              sx={{ position: 'absolute', top: 16, right: 16 }}
            >
              <Stack
                direction={'row'}
                spacing={1}
                sx={{
                  bgcolor: (t) => t.palette.grey[700],
                  backdropFilter: 'blur(8px)',
                  opacity: 0.8,
                  borderRadius: '4px 0 0 4px',
                }}
                zIndex={1}
                p={1}
              >
                <Stack alignItems={'center'}>
                  <IconButton
                    sx={{
                      color: (t) => t.palette.common.white,
                    }}
                    onClick={() => props.onClickInvite()}
                  >
                    <PersonAddIcon />
                  </IconButton>
                  <Typography
                    variant="caption"
                    sx={{ color: (t) => t.palette.common.white }}
                  >
                    {t('menu.invitation')}
                  </Typography>
                </Stack>
                <Stack alignItems={'center'}>
                  <IconButton
                    sx={{
                      color: (t) => t.palette.common.white,
                    }}
                    onClick={() => props.onClickClearMessages()}
                  >
                    <CleaningServicesIcon />
                  </IconButton>
                  <Typography
                    variant="caption"
                    sx={{ color: (t) => t.palette.common.white }}
                  >
                    {t('menu.claerScreen')}
                  </Typography>
                </Stack>
              </Stack>
              <Stack
                direction={'row'}
                spacing={1}
                sx={{
                  bgcolor: (t) => t.palette.grey[700],
                  backdropFilter: 'blur(8px)',
                  opacity: 0.8,
                  borderRadius: '0 4px 4px 0',
                }}
                zIndex={1}
                p={1}
              >
                <Stack alignItems={'center'}>
                  <IconButton
                    onClick={() => props.onClickLeaveSession()}
                    sx={{
                      color: (t) => t.palette.common.white,
                    }}
                  >
                    <LogoutIcon />
                  </IconButton>
                  <Typography
                    variant="caption"
                    sx={{ color: (t) => t.palette.common.white }}
                  >
                    {t('menu.leave')}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          )}

          <NetworkAlert scrollElement={chatbody} isOpened={isOpenedAlert} />
          <Virtuoso
            ref={virtuosoRef}
            id="chatbody"
            atBottomThreshold={100}
            atBottomStateChange={setIsAtBottom}
            atTopThreshold={300}
            atTopStateChange={setIsScrollTop}
            followOutput={(isAtBottom: boolean) =>
              isAtBottom ? 'smooth' : false
            }
            components={{
              // eslint-disable-next-line @typescript-eslint/naming-convention
              Header: () => (
                <Stack height={80} alignItems="center" justifyContent="center">
                  {props.loadingPrevious ? (
                    <CircularProgress color="primary" />
                  ) : props.canLoadPrevious ? (
                    <IconButton onClick={async () => loadPrevious()}>
                      <SyncIcon fontSize="large" />
                    </IconButton>
                  ) : null}
                </Stack>
              ),
              // eslint-disable-next-line @typescript-eslint/naming-convention
              Footer: () => <Box height="20px" />,
              // Scroller: CustomScrollbar
            }}
            style={{ height: '100%', width: '100%' }}
            data={adjustedMessages}
            firstItemIndex={adjustedMessages[0]?.[1].sequence}
            itemContent={(
              index: number,
              [key, msg]: [number, ChatMessageItem]
            ) => {
              const isMine = msg.userId === props.userId;
              const isSubUserSend = msg.isSubUserSend;
              const showReverse = isMine && props.reverseLanguage != null;
              let projectionMessages: ChatMessageLanguage[] = [];
              if (isProjectionMode) {
                projectionMessages = msg.languages.filter((x) =>
                  selectedProjectionLanguage.includes(x.language)
                );
              }
              const getMessageInfo = (isPlayingMessage?: boolean) => {
                if (isProjectionMode) {
                  return projectionMessages[0];
                }

                if (
                  props.canGuideControl &&
                  props.dualLangTranslation &&
                  isMine &&
                  isPlayingMessage
                ) {
                  // play translated message in dual mode
                  if (isSubUserSend) {
                    return msg.languages.find(
                      (x) => x.language === props.language
                    );
                  } else {
                    return msg.languages.find(
                      (x) => x.language === props.subUserLanguage
                    );
                  }
                } else {
                  if (isMine) {
                    return msg.languages.find((x) => x.isOriginal);
                  } else {
                    return msg.languages.find(
                      (x) => x.language === props.language
                    );
                  }
                }
              };
              const messageInfo = getMessageInfo();

              const getReverseMessage = (): string | undefined => {
                if (!msg.reverseMessage) return undefined;

                if (props.canGuideControl && props.dualLangTranslation) {
                  return props.isOnReverseTranslation
                    ? msg.reverseMessage
                    : undefined;
                }

                return showReverse
                  ? msg.reverseMessage ?? undefined
                  : undefined;
              };
              const user = props.getUser(msg.userId);
              const isSystem =
                msg.userId === '00000000-0000-0000-0000-000000000000';
              if (isSystem) {
                return (
                  <SystemMessage
                    command={msg.languages[0].message}
                    timestamp={new Date(msg.timestamp)}
                    canGuideControl={isChatHost(props.userRole)}
                    getUserName={getUserName}
                    isProjectionMode={isProjectionMode}
                  />
                );
              }

              if (
                hintIndex.current === 0 &&
                index >= visibleRange.startIndex &&
                isSystem === false
              ) {
                hintIndex.current = index;
              }

              return (
                <div
                  ref={(v) => {
                    if (hintIndex.current === index) {
                      setHintAnchor(v);
                      setHintIsRight(isMine);
                    }
                  }}
                >
                  <Comment
                    key={msg.sequence}
                    message={messageInfo?.message ?? ''}
                    reply={
                      msg.reply == null
                        ? undefined
                        : {
                            message:
                              msg.reply.languages.find(
                                (x) => x.language === props.language
                              )?.message ?? '',
                            userName:
                              props.getUser(msg.reply.userId)?.userName ??
                              'Unknown',
                          }
                    }
                    user={user}
                    userRole={props.userRole}
                    isGuestInputRestricted={props.isGuestInputRestricted}
                    isUserVisibled={
                      isProjectionMode ||
                      (isMine === false &&
                        props.getIsContinuedMessage(key, msg) === false)
                    }
                    timestamp={new Date(msg.timestamp)}
                    isMine={isMine}
                    isSubUserSend={msg.isSubUserSend}
                    isProjectionMode={isProjectionMode}
                    myMessagePosition={props.myMessagePosition}
                    canGuideControl={props.canGuideControl}
                    dualLangTranslation={props.dualLangTranslation}
                    projectionSubMessages={
                      projectionMessages?.[1]?.message ?? ''
                    }
                    isSending={
                      msg.id === '' &&
                      msg.error !== true &&
                      msg.isTyping === false
                    }
                    isTyping={msg.isTyping}
                    isError={msg.error === true}
                    // オリジナルの言語はあるが翻訳文はない場合翻訳エラーとする
                    isTranslateError={
                      messageInfo?.isOriginal === false &&
                      messageInfo.message === '' &&
                      msg.languages.find((l) => l.isOriginal)?.message !== ''
                    }
                    originalMessage={
                      isMine || messageInfo?.isOriginal === true
                        ? undefined
                        : msg.languages.find((l) => l.isOriginal)?.message
                    }
                    transedMessage={
                      showReverse
                        ? msg.languages.find(
                            (l) => l.language === msg.reverseLanguage
                          )?.message
                        : undefined
                    }
                    transedDualMessage={getTransedDualMessage(msg)}
                    reverseMessage={getReverseMessage()}
                    isRemoved={msg.isRemoved}
                    reactions={msg.reactions}
                    files={msg.files.map((x) => ({ id: x.id }))}
                    onEdit={() =>
                      props.onEditMessage(
                        messageInfo?.message ?? '',
                        msg.isSubUserSend
                      )
                    }
                    onReply={() =>
                      props.onReplyMessage(
                        msg.id,
                        user?.userId ?? '',
                        messageInfo?.message ?? '',
                        msg.isSubUserSend,
                        isMine
                      )
                    }
                    onDelete={async () => {
                      await props.onDeleteMessage(msg.id);
                    }}
                    onReaction={async (type) => {
                      await props.onReaction(msg.id, type);
                    }}
                    onPlay={
                      props.onPlayMessage
                        ? () => {
                            const messageToPlay = getMessageInfo(true);
                            if (messageToPlay && messageToPlay.message !== '') {
                              props.onPlayMessage?.(
                                messageToPlay.message,
                                messageToPlay.language,
                                msg.id,
                                messageToPlay.voiceRate ?? 1
                              );
                            }
                          }
                        : undefined
                    }
                    onStop={props.onStopMessage}
                    onChangeFlag={
                      props.onCheckFlag != null
                        ? (check) => props.onCheckFlag?.(msg.id, check)
                        : undefined
                    }
                    onShowMenu={() => {
                      setCommentMenuHintEnabled(false);
                    }}
                    chatRoomId={props.chatRoomId}
                    sessionEntryCode={props.sessionEntryCode}
                    isPlaying={msg.id === props.playingMessageId}
                    replaceNgWord={props.replaceNgWord}
                    flag={props.onCheckFlag != null ? msg.isMessageFlag : false}
                    onOpenReactions={
                      isChatEditor(props.isGuestInputRestricted, props.userRole)
                        ? () => {
                            const reactions = msg.reactions.map<ChatReaction>(
                              (x) => ({
                                userName:
                                  props.getUser(x.userId)?.userName ??
                                  'Unknown',
                                reactionType: x.reactionType,
                              })
                            );
                            props.onOpenReaction(reactions);
                          }
                        : undefined
                    }
                    reactionIsOnlyQuestion={props.reactionIsOnlyQuestion}
                    isHiddenInformation={props.isHiddenInformation}
                  />
                </div>
              );
            }}
            rangeChanged={(v) => {
              if (commentMenuHintEnabled) {
                setVisibleRange(v);
                hintIndex.current = 0;
              }
            }}
          />
        </Box>
        {!isAtBottom && (
          <Fab
            onClick={goToBottom}
            size="small"
            color="primary"
            sx={{
              opacity: 0.5,
              position: 'absolute',
              bottom: '12px',
              right: '16px',
            }}
          >
            <KeyboardArrowDownIcon />
          </Fab>
        )}
      </Box>

      {isChatEditor(props.isGuestInputRestricted, props.userRole) &&
        !isProjectionMode && (
          <HintBalloon
            caption={t('commentMenuHintMessage')}
            anchorEl={commentMenuHintEnabled ? hintAnchor : null}
            anchorPosition={hintIsRight ? 'RightBottom' : 'LeftBottom'}
            onClose={() => setCommentMenuHintEnabled(false)}
          />
        )}

      <LoadingBackdrop open={props.loading ?? false} />
    </>
  );
};
export default ChatBody;
